import { VERSAPAY_CLIENT_URL } from "../../../utils/versapayFrame";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Golfer } from "../../../types/types";
import {
  getProducts,
  SearchMembers,
  checkEmailExists,
  createOrUpdateMember,
  createVersapaySession,
  confirmVersapayPayment,
  getAdminOwners,
} from "../../../api/api-hook";
import {
  normalizeInput,
  formatDOB,
  convertFromISOToUS,
  isValidDOB,
  usaStates,
  usDateToTimestamp,
} from "../../../utils/utils";
import { getAdminSession } from "../../../utils/adminAuth";

// ── Types ────────────────────────────────────────────────────────────────────

interface VipProduct {
  id: string;
  name: string;
  label: string;
  price?: number;
  tabKey: string;
  product_id: string;
  bagTagPrice: number;
}

type Owner = { id: string; firstName: string; lastName: string; email: string };
type Step = 1 | 2 | 3 | 4 | 5;

// ── VersaPay (shared admin setup) ────────────────────────────────────────────

const VP_SCRIPT_ID = "versapay-sdk-script-admin";
const VP_SCRIPT_SRC = VERSAPAY_CLIENT_URL;

function startAdminVPTracking() {
  if ((window as any).__adminVpTrackingActive) return;
  (window as any).__adminVpTrackingActive = true;
  (window as any).__adminVpListeners = [];
  const _orig = window.addEventListener.bind(window);
  (window as any).addEventListener = function (type: string, listener: any, options?: any) {
    if (type === "message") ((window as any).__adminVpListeners as any[]).push([listener, options]);
    return _orig(type, listener, options);
  };
}

function removeAdminVPListeners() {
  const listeners: Array<[any, any]> = (window as any).__adminVpListeners || [];
  listeners.forEach(([l, opts]) =>
    window.removeEventListener("message", l as EventListener, opts as any)
  );
  (window as any).__adminVpListeners = [];
}

function killExistingAdminVP() {
  removeAdminVPListeners();
  document.getElementById(VP_SCRIPT_ID)?.remove();
  document.querySelectorAll('iframe[src*="versapay"], iframe[src*="ecommerce-api"]').forEach((el) => el.remove());
  try { (window as any).versapay = undefined; } catch (_) {}
}

async function loadAdminVPScript(): Promise<void> {
  startAdminVPTracking();
  killExistingAdminVP();
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.id = VP_SCRIPT_ID;
    s.src = `${VP_SCRIPT_SRC}?_t=${Date.now()}`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load VersaPay SDK."));
    document.body.appendChild(s);
  });
}

// ── Constants ────────────────────────────────────────────────────────────────

const BRAND_GREEN = "#023822";
const WV_TAB_KEY = "wv-vip-card";

const STEP_LABELS: Record<Step, string> = {
  1: "Member",
  2: "Personal Info",
  3: "Address",
  4: "Payment",
  5: "Done",
};

// ── Sub-components ───────────────────────────────────────────────────────────

function StepBreadcrumb({ current }: { current: Step }) {
  const steps: Step[] = [1, 2, 3, 4, 5];
  return (
    <div className="d-flex align-items-center gap-1 mb-4 flex-wrap" style={{ fontSize: "0.8rem" }}>
      {steps.map((s) => (
        <React.Fragment key={s}>
          <span style={{
            padding: "0.2rem 0.6rem",
            borderRadius: 4,
            backgroundColor: s === current ? BRAND_GREEN : s < current ? "#c3e6cb" : "#e9ecef",
            color: s === current ? "#fff" : s < current ? "#155724" : "#6c757d",
            fontWeight: s === current ? 600 : 400,
          }}>
            {STEP_LABELS[s]}
          </span>
          {s < 5 && <span style={{ color: "#adb5bd" }}>›</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-light border px-3 py-2 mb-4 text-uppercase fw-semibold" style={{ fontSize: "0.9rem" }}>
      {children}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const EMPTY: Golfer = {
  firstName: "", lastName: "", email: "", phone: "", sex: "",
  date_of_birth: "", address: "", street_address_2: "", city: "",
  state: "VA", zip: "", bagTag: false, donation: false, personalization: "",
  donation_amount: 0, vip_card_type: "WV VIP Card", vip_package: WV_TAB_KEY,
  memberNumber: "", cart_total: 0, quantity: "1", sameAsBilling: true,
  ShippingAddress: "", ShippingStreet_address_2: "", ShippingCity: "",
  ShippingState: "", ShippingZip: "", product_id: "", GiftPurchase: false,
  GiftProductId: "", GiftVipPackage: "", ghinNumber: "", bagTagPrice: 0,
  promo_value: 0, promo_amount_applied: 0, promo_object_id: 0, promo_code: "", ownerId: "",
};

const WvVipCard = () => {
  const [step, setStep] = useState<Step>(1);
  const [fd, setFd] = useState<Golfer>({ ...EMPTY });
  const update = <K extends keyof Golfer>(field: K, value: Golfer[K]) =>
    setFd((prev) => ({ ...prev, [field]: value }));

  // ── Product + owners ──────────────────────────────────────────────────────
  const [product, setProduct] = useState<VipProduct | null>(null);
  const [productLoading, setProductLoading] = useState(true);
  const [owners, setOwners] = useState<Owner[]>([]);
  const sessionOwnerId = getAdminSession()?.ownerId ?? null;

  useEffect(() => {
    if (sessionOwnerId) setFd((prev) => ({ ...prev, ownerId: sessionOwnerId }));

    setProductLoading(true);
    getProducts({ property: "tabKey", value: WV_TAB_KEY })
      .then((raw) => {
        const data: VipProduct[] = typeof raw === "string" ? JSON.parse(raw) : raw;
        const p = data[0] ?? null;
        setProduct(p);
        if (p) {
          setFd((prev) => ({ ...prev, product_id: p.product_id, vip_package: p.tabKey }));
        }
      })
      .finally(() => setProductLoading(false));

    getAdminOwners().then((r) => { if (r.success) setOwners(r.owners); });
  }, []);

  // ── Step 1: Member search ─────────────────────────────────────────────────
  const [searchResults, setSearchResults] = useState<Golfer[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSearched(true);
    setSearchLoading(true);
    try {
      const raw = await SearchMembers(fd.lastName || "", fd.memberNumber || "");
      const data: Golfer[] = typeof raw === "string" ? JSON.parse(raw) : raw;
      setSearchResults(Array.isArray(data) ? data : []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectMember = (member: Golfer) => {
    setFd((prev) => ({
      ...prev,
      ...member,
      vip_package: WV_TAB_KEY,
      vip_card_type: "WV VIP Card",
      product_id: prev.product_id,
      ownerId: prev.ownerId,
      date_of_birth: member.date_of_birth
        ? member.date_of_birth.includes("-")
          ? convertFromISOToUS(member.date_of_birth)
          : member.date_of_birth
        : "",
      phone: normalizeInput(member.phone),
    }));
    setStep(2);
  };

  const handleNewMember = () => {
    setFd((prev) => ({
      ...EMPTY,
      vip_package: WV_TAB_KEY,
      vip_card_type: "WV VIP Card",
      product_id: prev.product_id,
      ownerId: prev.ownerId,
    }));
    setStep(2);
  };

  // ── Step 2: Personal info ─────────────────────────────────────────────────
  const [emailStatus, setEmailStatus] = useState<"idle" | "checking" | "error">("idle");
  const [emailMessage, setEmailMessage] = useState("");
  const [dobError, setDobError] = useState("");

  const handleEmailBlur = async () => {
    const email = fd.email || "";
    if (!email || !/\S+@\S+\.\S+/.test(email)) { setEmailStatus("idle"); setEmailMessage(""); return; }
    setEmailStatus("checking");
    try {
      const raw = await checkEmailExists(email);
      const data = JSON.parse(raw || "{}");
      if (data.exists && data.contact?.id != fd.id) {
        setEmailStatus("error");
        setEmailMessage("That email is already taken.");
      } else {
        setEmailStatus("idle"); setEmailMessage("");
      }
    } catch {
      setEmailStatus("idle"); setEmailMessage("");
    }
  };

  const handleDOBChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const formatted = formatDOB(e.currentTarget.value);
    update("date_of_birth", formatted);
    if (formatted.length !== 10 || !isValidDOB(formatted)) {
      setDobError("Enter a valid date (MM/DD/YYYY)");
    } else {
      setDobError("");
    }
  };

  // ── Derived totals (always $0) ────────────────────────────────────────────
  const finalTotal = 0;
  const finalTotalCents = 0;

  const clearVPSession = useCallback(() => {
    if (containerRef.current) containerRef.current.innerHTML = "";
    clientRef.current = null;
    frameMountedRef.current = false;
    callbackBoundSessionRef.current = "";
    mountedSessionRef.current = "";
    sessionIdRef.current = "";
    sessionAmountRef.current = 0;
  }, []);

  // ── VersaPay refs (kept for structural parity, VP won't init for $0) ──────
  const containerRef = useRef<HTMLDivElement | null>(null);
  const clientRef = useRef<any>(null);
  const sessionIdRef = useRef("");
  const sessionAmountRef = useRef(0);
  const frameMountedRef = useRef(false);
  const mountedSessionRef = useRef("");
  const callbackBoundSessionRef = useRef("");
  const mountedRef = useRef(true);
  const processingApprovalRef = useRef(false);
  const approvalReceivedRef = useRef(false);
  const handleApprovalRef = useRef<((result: any, sid: string) => Promise<void>) | null>(null);
  const clearVPSessionRef = useRef<() => void>(() => {});
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fdRef = useRef(fd);
  fdRef.current = fd;
  const finalTotalRef = useRef(finalTotal);
  finalTotalRef.current = finalTotal;
  const finalTotalCentsRef = useRef(finalTotalCents);
  finalTotalCentsRef.current = finalTotalCents;

  const [vpLoading, setVpLoading] = useState(false);
  const [vpError, setVpError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [completedGolfer, setCompletedGolfer] = useState<Golfer | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [miscNote, setMiscNote] = useState("");

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleApproval = useCallback(async (result: any, sessionToUse: string) => {
    approvalReceivedRef.current = true;
    if (processingApprovalRef.current) return;
    try {
      processingApprovalRef.current = true;
      setProcessingPayment(true); setVpLoading(true); setVpError(null);

      const currentFd = fdRef.current;

      const confirmResponse = await confirmVersapayPayment({
        sessionId: sessionToUse,
        approvalResult: result,
        amount: 0,
        firstName: currentFd.firstName,
        lastName: currentFd.lastName,
        email: currentFd.email,
        address1: currentFd.address,
        city: currentFd.city,
        state: currentFd.state,
        zip: currentFd.zip,
        vipCardNumber: currentFd.ghinNumber || "",
        lineItems: [{ type: "Item", number: currentFd.product_id || "WV-VIP", description: "WV VIP Card", price: 0, quantity: 1, discount: 0 }],
      } as any);

      if (!confirmResponse?.success) throw new Error(confirmResponse?.error || "Failed to confirm payment.");

      const updatedGolfer: Golfer = {
        ...currentFd,
        cart_total: 0,
        sameAsBilling: true,
        GiftPurchase: false,
        versapaySessionId: sessionToUse as any,
        versapayApprovalResult: result as any,
        versapayTransactionId: confirmResponse.transactionId as any,
        versapayOrderNumber: (confirmResponse.orderNumber ?? "") as any,
        versapayVpOrderNumber: (confirmResponse.vpOrderNumber ?? "") as any,
        ccToken: (confirmResponse.ccToken ?? "") as any,
        ccLastFour: (confirmResponse.ccLastFour ?? "") as any,
        ccType: (confirmResponse.ccType ?? "") as any,
      };

      const hubspotPayload = {
        ...updatedGolfer,
        payment_method: "Credit Card",
        date_of_birth: updatedGolfer.date_of_birth ? usDateToTimestamp(updatedGolfer.date_of_birth) : undefined,
        created_source: "Admin",
};

      await createOrUpdateMember(hubspotPayload as any);
      setCompletedGolfer(updatedGolfer);
      setStep(5);
    } catch (err: any) {
      const status = err?.response?.status;
      const bodyText: string = err?.response?.data ?? err?.message ?? "";
      const is504 = status === 504 || (typeof bodyText === "string" && bodyText.includes("504"));
      setPayError(is504
        ? "The request timed out. Please wait and try again."
        : err?.response?.data?.error || err?.response?.data?.message || err?.message || "Payment approved but processing failed.");
    } finally {
      processingApprovalRef.current = false;
      setProcessingPayment(false); setVpLoading(false);
    }
  }, []);

  handleApprovalRef.current = handleApproval;
  clearVPSessionRef.current = clearVPSession;

  const handleFreeOrder = async () => {
    if (vpLoading || processingPayment) return;
    setProcessingPayment(true); setVpLoading(true); setVpError(null);
    try {
      const hubspotPayload = {
        ...fdRef.current,
        cart_total: 0,
        payment_method: "Credit Card",
        date_of_birth: fdRef.current.date_of_birth ? usDateToTimestamp(fdRef.current.date_of_birth) : undefined,
        ...(miscNote.trim() ? { misc_note: miscNote.trim() } : {}),
      };
      await createOrUpdateMember(hubspotPayload as any);
      setCompletedGolfer(fdRef.current);
      setStep(5);
    } catch (err: any) {
      setPayError(err?.response?.data?.error || err?.message || "Order processing failed.");
    } finally {
      setProcessingPayment(false); setVpLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleStartOver = () => {
    clearVPSession();
    (window as any).__adminVpSessionWasUsed = false;
    approvalReceivedRef.current = false;
    setFd({ ...EMPTY, product_id: product?.product_id || "", vip_package: WV_TAB_KEY });
    setStep(1);
    setCompletedGolfer(null); setPayError(null); setVpError(null); setMiscNote("");
    setHasSearched(false); setSearchResults([]);
    setEmailStatus("idle"); setEmailMessage(""); setDobError("");
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <h4 className="mb-1" style={{ color: BRAND_GREEN }}>WV VIP Card</h4>
      {product && !productLoading && (
        <p className="text-muted small mb-1">
          Process a WV VIP Card — <strong>{product.name}</strong> at <strong>$0.00</strong>.
        </p>
      )}
      {productLoading && <p className="text-muted small mb-1">Loading product…</p>}
      {fd.ownerId && owners.length > 0 && (
        <p className="text-muted small mb-4">
          Owner: <strong>{owners.find((o) => o.id === fd.ownerId)?.firstName} {owners.find((o) => o.id === fd.ownerId)?.lastName}</strong>
        </p>
      )}

      <StepBreadcrumb current={step} />

      {/* ── STEP 1: Member Search ── */}
      {step === 1 && (
        <div>
          <SectionHeader>Find Member</SectionHeader>

          <div className="mb-4" style={{ maxWidth: 360 }}>
            <label className="form-label fw-semibold">Assigned Owner</label>
            {sessionOwnerId && owners.length > 0 ? (
              <div className="form-control bg-light text-muted" style={{ cursor: "default" }}>
                {(() => { const o = owners.find((o) => o.id === sessionOwnerId); return o ? `${o.firstName} ${o.lastName}` : "Loading…"; })()}
              </div>
            ) : (
              <select
                className="form-select"
                value={fd.ownerId || ""}
                onChange={(e) => update("ownerId", e.target.value as any)}
              >
                <option value="">— No owner —</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.firstName} {o.lastName}{o.email ? ` (${o.email})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <form className="row gy-3 mb-4" onSubmit={handleSearch}>
            <div className="col-md-4">
              <label className="form-label">Last Name</label>
              <input type="text" className="form-control" value={fd.lastName || ""}
                onChange={(e) => update("lastName", e.target.value)} placeholder="Last name" required />
            </div>
            <div className="col-md-4">
              <label className="form-label">VIP Card # or GHIN #</label>
              <input type="text" className="form-control" value={fd.memberNumber || ""}
                onChange={(e) => update("memberNumber", e.target.value.replace(/\D/g, ""))}
                placeholder="VIP or GHIN number" required />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button type="submit" className="btn btn-primary w-100" disabled={searchLoading}>
                {searchLoading ? "Searching…" : "Search"}
              </button>
            </div>
          </form>

          {hasSearched && searchResults.length > 0 && (
            <div className="table-responsive mb-4">
              <table className="table table-sm table-hover align-middle" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f1f3f5" }}>
                    <th>First Name</th><th>Last Name</th><th>Email</th><th>Address</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((r, i) => (
                    <tr key={i}>
                      <td>{r.firstName}</td><td>{r.lastName}</td>
                      <td>{r.email || "—"}</td><td>{r.address || "—"}</td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleSelectMember(r)}>Select →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasSearched && !searchLoading && searchResults.length === 0 && (
            <div className="alert alert-warning small">No results found.</div>
          )}

          <div className="text-center mb-4">
            <p className="small text-muted mb-2">New member? Create a fresh account.</p>
            <button type="button" className="btn btn-outline-secondary btn-sm px-4" onClick={handleNewMember}>
              Create New Account
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Personal Info ── */}
      {step === 2 && (
        <div>
          <SectionHeader>Personal Information</SectionHeader>
          {fd.ghinNumber && (
            <div className="alert alert-light d-flex align-items-center small mb-4">
              <span className="me-2">ℹ</span>
              Updates here will also update the member's GHIN profile.
            </div>
          )}
          <form className="row gy-3"
            onSubmit={(e) => { e.preventDefault(); if (emailStatus === "error" || dobError) return; setStep(3); }}>
            <div className="col-md-6">
              <label className="form-label">First Name</label>
              <input type="text" className="form-control" value={fd.firstName || ""}
                onChange={(e) => update("firstName", e.target.value)} required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Last Name</label>
              <input type="text" className="form-control" value={fd.lastName || ""}
                onChange={(e) => update("lastName", e.target.value)} required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Email</label>
              <input type="email" className="form-control" value={fd.email || ""}
                onChange={(e) => update("email", e.target.value)}
                onBlur={handleEmailBlur} placeholder="email@example.com" required />
              {emailStatus !== "idle" && (
                <small className={`form-text ${emailStatus === "error" ? "text-danger" : "text-muted"}`}>
                  {emailMessage}
                </small>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Phone</label>
              <input type="tel" className="form-control" value={fd.phone || ""}
                onChange={(e) => update("phone", e.target.value)}
                onKeyUp={(e) => update("phone", normalizeInput(e.currentTarget.value))}
                placeholder="(555) 555-5555" pattern="[0-9\(\)\-\s]{14,14}" minLength={14} maxLength={14} required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Gender</label>
              <select className="form-select" value={fd.sex || ""}
                onChange={(e) => update("sex", e.target.value)} required>
                <option value="">Select gender…</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Date of Birth</label>
              <input type="text" className={`form-control${dobError ? " is-invalid" : ""}`}
                value={fd.date_of_birth || ""}
                onChange={(e) => update("date_of_birth", e.target.value)}
                onKeyUp={handleDOBChange} placeholder="MM/DD/YYYY" required />
              {dobError && <div className="text-danger small mt-1">{dobError}</div>}
            </div>
            <div className="col-12 d-flex justify-content-between mt-4">
              <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(1)}>← Back</button>
              <button type="submit" className="btn btn-primary px-4"
                disabled={emailStatus === "error" || emailStatus === "checking" || !!dobError}>
                Next →
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 3: Address ── */}
      {step === 3 && (
        <div>
          <SectionHeader>Address Information</SectionHeader>
          {fd.ghinNumber && (
            <div className="alert alert-light d-flex align-items-center small mb-4">
              <span className="me-2">ℹ</span>
              Updates here will also update the member's GHIN profile.
            </div>
          )}
          <form className="row gy-3" onSubmit={(e) => { e.preventDefault(); killExistingAdminVP(); setStep(4); }}>
            <div className="col-12">
              <label className="form-label">Street Address</label>
              <input type="text" className="form-control" value={fd.address || ""}
                onChange={(e) => update("address", e.target.value)} placeholder="123 Main St" required />
            </div>
            <div className="col-12">
              <label className="form-label">Address Line 2</label>
              <input type="text" className="form-control" value={fd.street_address_2 || ""}
                onChange={(e) => update("street_address_2", e.target.value)} placeholder="Suite, apt, etc. (optional)" />
            </div>
            <div className="col-md-5">
              <label className="form-label">City</label>
              <input type="text" className="form-control" value={fd.city || ""}
                onChange={(e) => update("city", e.target.value)} placeholder="Richmond" required />
            </div>
            <div className="col-md-4">
              <label className="form-label">State</label>
              <select className="form-select" value={fd.state || "VA"}
                onChange={(e) => update("state", e.target.value)} required>
                <option value="">Select state…</option>
                {usaStates.map((s, i) => <option key={i} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">ZIP</label>
              <input type="text" className="form-control" value={fd.zip || ""}
                onChange={(e) => update("zip", e.target.value.replace(/\D/g, ""))}
                onKeyUp={(e) => update("zip", e.currentTarget.value.replace(/\D/g, ""))}
                placeholder="ZIP" maxLength={5} inputMode="numeric" pattern="[0-9]*" required />
            </div>
            <div className="col-12 d-flex justify-content-between mt-4">
              <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(2)}>← Back</button>
              <button type="submit" className="btn btn-primary px-4">Next →</button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 4: Payment ── */}
      {step === 4 && (
        <div>
          <SectionHeader>Payment</SectionHeader>

          <div className="row g-4">
            {/* Left: form */}
            <div className="col-lg-7">
              <h6 className="text-uppercase mb-3">Contact Information</h6>
              <div className="row gy-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label small">First Name</label>
                  <input type="text" className="form-control" value={fd.firstName || ""}
                    onChange={(e) => update("firstName", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Last Name</label>
                  <input type="text" className="form-control" value={fd.lastName || ""}
                    onChange={(e) => update("lastName", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Email</label>
                  <input type="email" className="form-control" value={fd.email || ""}
                    onChange={(e) => update("email", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Phone</label>
                  <input type="tel" className="form-control" value={fd.phone || ""}
                    onChange={(e) => update("phone", e.target.value)} />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label small fw-semibold">Note <span className="text-muted fw-normal">(optional)</span></label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter note"
                  value={miscNote}
                  onChange={(e) => setMiscNote(e.target.value)}
                />
              </div>

              {(vpError || payError) && (
                <div className="alert alert-danger mt-3 small" role="alert">
                  {vpError || payError}
                </div>
              )}

              <div className="mt-3">
                <button type="button" className="btn btn-primary w-100 py-2"
                  disabled={processingPayment}
                  onClick={handleFreeOrder}>
                  {processingPayment ? "Processing…" : "Complete Order"}
                </button>
              </div>
              <div className="mt-3">
                <button type="button" className="btn btn-link text-decoration-none px-0"
                  onClick={() => setStep(3)}>← Back</button>
              </div>
            </div>

            {/* Right: order summary */}
            <div className="col-lg-5">
              <div className="border rounded p-4 bg-white">
                <h6 className="text-uppercase mb-3">Order Summary</h6>

                {product && (
                  <div className="d-flex justify-content-between small mb-2">
                    <span><strong>{product.name}</strong></span>
                    <span>$0.00</span>
                  </div>
                )}

                <hr className="my-2" />

                <div className="d-flex justify-content-between fw-bold small mb-3">
                  <span>Total</span>
                  <span>$0.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 5: Done ── */}
      {step === 5 && completedGolfer && (
        <div>
          <div className="text-center py-4">
            <div style={{ fontSize: "3rem", color: BRAND_GREEN }}>✓</div>
            <h5 className="mt-2 fw-semibold" style={{ color: BRAND_GREEN }}>Order Complete</h5>
            <p className="text-muted small">
              The WV VIP Card has been successfully recorded in HubSpot.
            </p>
          </div>

          <div className="border rounded p-4 mb-4" style={{ maxWidth: 480, margin: "0 auto" }}>
            <h6 className="text-uppercase small fw-semibold mb-3" style={{ color: BRAND_GREEN }}>
              Order Details
            </h6>
            <div className="row gy-2 small">
              <div className="col-5 text-muted">Member</div>
              <div className="col-7 fw-semibold">{completedGolfer.firstName} {completedGolfer.lastName}</div>
              <div className="col-5 text-muted">Email</div>
              <div className="col-7">{completedGolfer.email}</div>
              <div className="col-5 text-muted">Product</div>
              <div className="col-7">{product?.name || "WV VIP Card"}</div>
              <div className="col-5 text-muted">Amount</div>
              <div className="col-7">$0.00</div>
              {fd.ownerId && owners.length > 0 && (
                <>
                  <div className="col-5 text-muted">Assigned to</div>
                  <div className="col-7">
                    {owners.find((o) => o.id === fd.ownerId)?.firstName}{" "}
                    {owners.find((o) => o.id === fd.ownerId)?.lastName}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="text-center">
            <button type="button" className="btn btn-primary px-5" onClick={handleStartOver}>
              Process Another WV VIP Card
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WvVipCard;
